import { useLogto } from '@logto/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createApiClient, type PendingRequest } from '../api/client';
import { ErrorBanner } from '../ui/ErrorBanner';
import { channelLabel, formatCaboVerdeDayTime, formatDateTime, sortByDeparture, telHref } from '../ui/format';

export function Requests() {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await api.getPendingRequests();
      setRequests(list);
      setError(null);
      // Keep the sidebar "Pedidos (n)" badge in sync after a confirm/decline, not only on a page reload.
      window.dispatchEvent(new CustomEvent('morabi:pending-changed', { detail: list.length }));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(id: string, kind: 'confirm' | 'decline') {
    setBusy(id);
    setError(null);
    try {
      if (kind === 'confirm') {
        await api.confirmRequest(id);
      } else {
        await api.declineRequest(id);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Oldest departure first, so the operator works the most imminent requests at the top.
  const sorted = useMemo(() => sortByDeparture(requests), [requests]);

  return (
    <section className="page">
      <header className="page-head">
        <h1>Pedidos</h1>
        <p className="subtle">Pedidos de reserva do site à espera da sua confirmação.</p>
      </header>
      <ErrorBanner message={error} />

      {requests.length === 0 && !error ? <div className="card subtle">Sem pedidos pendentes.</div> : null}

      {sorted.map((r) => (
        <section className="card request-card" key={r.id}>
          <div className="request-head">
            <div>
              <div className="request-time">{formatCaboVerdeDayTime(r.startsAt)}</div>
              <div className="subtle">{r.tourTitle}</div>
            </div>
            <span className="chip">{r.pax} pax</span>
          </div>
          <div className="request-body">
            <div className="request-name">
              {r.customerName ?? '—'}
              {r.matchedExistingCustomer ? <span className="chip chip-muted">Corresponde a um cliente existente</span> : null}
            </div>
            <div className="request-contacts subtle">
              {r.phone ? <a href={telHref(r.phone)}>{r.phone}</a> : null}
              {r.email ? <a href={`mailto:${encodeURIComponent(r.email)}`}>{r.email}</a> : null}
              <span>{channelLabel(r.channel)}</span>
              {r.language ? <span>{r.language.toUpperCase()}</span> : null}
            </div>
            <div className="subtle">Recebido {formatDateTime(r.createdAt)}</div>
          </div>
          <div className="request-actions">
            <button className="btn btn-primary" disabled={busy === r.id} onClick={() => void act(r.id, 'confirm')}>Confirmar</button>
            <button className="btn" disabled={busy === r.id} onClick={() => void act(r.id, 'decline')}>Recusar</button>
          </div>
        </section>
      ))}
    </section>
  );
}
